/**
 * Unit medallions — model-derived animated profile portraits.
 *
 * There is no portrait art in the UE data or the art manifest, so each
 * medallion renders the unit's OWN 3D model to a small offscreen viewport
 * (render-to-texture), cached per unit type. The model is built by the same
 * pipeline the game renders, so a medallion can never drift from the mapped
 * model:
 *
 * - No vendored GLB yet: the procedural skinned humanoid from
 *   render/skinned.ts, faction-tinted exactly like the in-world units.
 * - GLB vendored (public/assets/manifest.json lists it): the loaded GLB
 *   scene renders instead — resolved through the ModelStore from the
 *   spec's ResolvedModel. The procedural portrait shows until the async
 *   load completes, then the cached frame is re-rendered and every canvas
 *   currently showing that medallion is repainted.
 *
 * The cache key already partitions on the art key (canonical id + echelon
 * band, see data/artmap.ts), so medallions follow model progression
 * automatically. Training buttons and faction-select signature medallions
 * share this cache path via the spec's `model` field.
 *
 * User spec:
 * - The animation plays ONCE on first selection, then stays static.
 * - Training buttons show the same medallions (static).
 * - Units with no mapped model get a clean placeholder — never a crash,
 *   never a blank.
 *
 * Texture-memory budget (documented, not aspirational):
 * - One cached medallion = 128x128 RGBA canvas = 64 KiB.
 * - Full roster (72 unit types across the three factions) x 2 tints worst
 *   case ~= 9 MiB; realistic (player faction + enemies seen) ~= 4.5 MiB.
 * - The bone atlas texture is SHARED with the main renderer (three.js
 *   uploads it once per GL context; ~384 KiB for the current clip set).
 * - Intro animation allocates nothing per frame: one 128x128 GL scratch
 *   canvas is reused and blitted to the UI with drawImage.
 * - Cost: one extra WebGL context (the medallion scratch renderer). Kept
 *   at 128x128, disposed never (lives with the game session).
 *
 * Headless/testable by construction: cache-key logic, the play-once
 * tracker, and the 2D placeholder are pure and covered in
 * tests/medallions.test.ts. WebGL work is isolated in ensureGL()/render
 * paths and degrades to the 2D placeholder on any failure.
 */
import * as THREE from "three";
import {
  BONE_COUNT,
  FRAMES_PER_CLIP,
  AnimController,
  type AnimActivity,
  type AnimSystemLike,
} from "./anim";
import {
  attachAnimAttributes,
  buildHumanoidGeometry,
  flagAnimAttributes,
  makeSkinnedMaterial,
  writeAnimPose,
  type AnimAttributes,
} from "./skinned";
import type { ResolvedModel } from "../data/artmap";
import type { ModelStore } from "./models";

/** Render resolution of the offscreen portrait (UI scales it down). */
export const MEDALLION_PX = 128;
/** Intro animation length on first selection (seconds). */
export const INTRO_SECONDS = 1.6;

/** What a medallion depicts. */
export interface MedallionSpec {
  /** artKeyFor(db.unitModel(id), id) — embeds canonical id + echelon band. */
  artKey: string;
  /** Unit display name (used by the 2D fallback portrait). */
  name: string;
  /** Faction tint, as a hex number (same tint the in-world units wear). */
  tint: number;
  /**
   * Resolved art for the unit (db.unitModel(id)). When its GLB is vendored
   * and a ModelStore is wired, the portrait renders the real model;
   * otherwise the procedural humanoid. Optional so older call sites keep
   * working with the procedural fallback.
   */
  model?: ResolvedModel;
}

/** Animation source the medallions need: the game's clip registry + atlas. */
export interface MedallionAnimSource extends AnimSystemLike {
  texture: THREE.DataTexture;
}

/**
 * Cache key: the art key plus the faction tint (the tint is part of the
 * portrait). The art key already embeds unit type, canonical model id and
 * echelon band, so medallions follow model progression automatically.
 */
export function medallionCacheKey(spec: MedallionSpec): string {
  return `${spec.artKey}|tint${spec.tint.toString(16)}`;
}

/**
 * Play-once state: which medallions have already played their intro.
 * Pure — headless testable.
 */
export class IntroTracker {
  private played = new Set<string>();

  shouldPlay(spec: MedallionSpec): boolean {
    return !this.played.has(medallionCacheKey(spec));
  }

  markPlayed(spec: MedallionSpec): void {
    this.played.add(medallionCacheKey(spec));
  }

  playedKeys(): string[] {
    return [...this.played];
  }
}

/**
 * Clean 2D placeholder portrait: faction-tinted disc, gold ring, and the
 * unit's initial. Used when WebGL is unavailable or a render fails — never
 * a crash, never a blank canvas. Pure 2D; headless testable with a mock
 * context.
 */
export function renderPlaceholderPortrait(
  ctx: CanvasRenderingContext2D,
  name: string,
  tint: number,
  px: number,
): void {
  const tintCss = `#${tint.toString(16).padStart(6, "0")}`;
  const c = px / 2;
  ctx.save();
  ctx.clearRect(0, 0, px, px);
  // Disc.
  const grad = ctx.createRadialGradient(c, c * 0.8, px * 0.1, c, c, c);
  grad.addColorStop(0, "#2a2d33");
  grad.addColorStop(1, "#141518");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(c, c, c - 1, 0, Math.PI * 2);
  ctx.fill();
  // Faction-tinted ring.
  ctx.strokeStyle = tintCss;
  ctx.lineWidth = Math.max(2, px * 0.03);
  ctx.beginPath();
  ctx.arc(c, c, c - ctx.lineWidth, 0, Math.PI * 2);
  ctx.stroke();
  // Unit initial.
  const initial = (name.trim()[0] ?? "?").toUpperCase();
  ctx.fillStyle = "#e8e2d4";
  ctx.font = `600 ${Math.round(px * 0.44)}px Georgia, serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(initial, c, c + px * 0.03);
  ctx.restore();
}

interface PortraitSubject {
  holder: THREE.Group;
  /** True while the subject is the procedural humanoid (bone-posible). */
  procedural: boolean;
  attrs: AnimAttributes | null;
  mat: THREE.MeshStandardMaterial | null;
  /**
   * Real-clip mixer for GLB subjects whose export vendors animation clips.
   * The intro advances it each frame (the GLB plays its own skeletal clip
   * natively — no retarget needed); null for procedural subjects and for
   * GLBs without clips (they keep the whole-model profile turn only).
   */
  mixer: THREE.AnimationMixer | null;
}

interface PortraitGL {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  group: THREE.Group;
  /** One subject holder per medallion cache key; only one visible. */
  subjects: Map<string, PortraitSubject>;
  /** UI canvases currently showing each key (repainted on GLB upgrade). */
  attached: Map<string, Set<HTMLCanvasElement>>;
}

/**
 * Offscreen medallion renderer. Owns one small WebGL context, the portrait
 * scene, the static-frame cache, and the play-once intro tracker.
 *
 * Constructing is cheap and DOM/GL-free (the context is created lazily on
 * first use), so this class is safe to instantiate in headless tests.
 */
export class MedallionRenderer {
  readonly intro = new IntroTracker();
  private cache = new Map<string, HTMLCanvasElement>();
  private gl: PortraitGL | null = null;
  private glFailed = false;
  /** Generation counter per UI canvas: a newer attach cancels an intro. */
  private introGen = new WeakMap<HTMLCanvasElement, number>();

  constructor(
    private readonly anim: MedallionAnimSource,
    private readonly store: ModelStore | null = null,
  ) {}

  /** True once the scratch GL context exists and renders. */
  webglAvailable(): boolean {
    return this.ensureGL();
  }

  cachedKeys(): string[] {
    return [...this.cache.keys()];
  }

  introsPlayed(): string[] {
    return this.intro.playedKeys();
  }

  /** Bytes held by the static cache (budget accounting). */
  cacheBytes(): number {
    return this.cache.size * MEDALLION_PX * MEDALLION_PX * 4;
  }

  dispose(): void {
    this.gl?.renderer.dispose();
    this.gl = null;
    this.cache.clear();
  }

  /**
   * Show the STATIC medallion on a UI canvas (training buttons, repeat
   * selections). Blits the cached frame; renders + caches on miss.
   * Never throws; falls back to the 2D placeholder on any GL failure.
   */
  attachStatic(canvas: HTMLCanvasElement | null, spec: MedallionSpec): void {
    if (!canvas) return;
    this.introGen.set(canvas, (this.introGen.get(canvas) ?? 0) + 1);
    const key = medallionCacheKey(spec);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const cached = this.cache.get(key);
    if (cached) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(cached, 0, 0, canvas.width, canvas.height);
      return;
    }
    if (this.ensureGL()) {
      // Track AFTER the GL context exists so GLB upgrades can repaint us.
      this.trackAttachment(key, canvas);
      try {
        const frame = this.renderFrame(0.45, -0.12, spec.tint, spec);
        this.cache.set(key, frame);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
        return;
      } catch {
        this.glFailed = true;
        this.gl = null;
      }
    }
    renderPlaceholderPortrait(ctx, spec.name, spec.tint, canvas.width);
  }

  /**
   * Show the medallion on a UI canvas with the play-once intro: the first
   * time this unit type is selected, its idle animation plays (with a slow
   * profile turn) for ~INTRO_SECONDS, then freezes on the final frame,
   * which becomes the cached static frame. Later selections blit static.
   */
  attachWithIntro(canvas: HTMLCanvasElement | null, spec: MedallionSpec): void {
    if (!canvas) return;
    if (!this.intro.shouldPlay(spec) || !this.ensureGL()) {
      if (!this.ensureGL()) this.intro.markPlayed(spec);
      this.attachStatic(canvas, spec);
      return;
    }
    const gen = (this.introGen.get(canvas) ?? 0) + 1;
    this.introGen.set(canvas, gen);
    const key = medallionCacheKey(spec);
    this.trackAttachment(key, canvas);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      this.intro.markPlayed(spec);
      return;
    }
    const g = this.gl!;
    // The intro poses the subject every frame — make sure it exists first.
    this.ensureSubject(spec);
    // Fresh controller per intro so the sweep always starts identically.
    const sweep = new AnimController(this.anim, false);
    const startRot = -0.6;
    const endRot = -0.12;
    const t0 = performance.now();
    let last = t0;
    const step = (now: number): void => {
      if (this.introGen.get(canvas) !== gen) return; // superseded
      const el = Math.min(INTRO_SECONDS, (now - t0) / 1000);
      const dt = Math.min(0.1, Math.max(0.001, (now - last) / 1000));
      last = now;
      const pose = sweep.update(dt, "idle");
      const ease = el / INTRO_SECONDS;
      const rotY = startRot + (endRot - startRot) * (ease * ease * (3 - 2 * ease));
      this.poseFrame(g, spec, pose.a, pose.timeA, pose.b, pose.timeB, pose.blend, rotY, dt);
      g.renderer.render(g.scene, g.camera);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(g.renderer.domElement, 0, 0, canvas.width, canvas.height);
      if (el < INTRO_SECONDS) {
        requestAnimationFrame(step);
        return;
      }
      // Freeze: cache the final frame as the static medallion.
      try {
        const frame = this.renderFrame(0.45 + INTRO_SECONDS, endRot, spec.tint, spec);
        this.cache.set(medallionCacheKey(spec), frame);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
      } catch {
        this.glFailed = true;
        this.gl = null;
        renderPlaceholderPortrait(ctx, spec.name, spec.tint, canvas.width);
      }
      this.intro.markPlayed(spec);
    };
    requestAnimationFrame(step);
  }

  // ------------------------------------------------------------------
  // Internals
  // ------------------------------------------------------------------

  private trackAttachment(key: string, canvas: HTMLCanvasElement): void {
    const g = this.gl;
    if (!g) return;
    let set = g.attached.get(key);
    if (!set) {
      set = new Set();
      g.attached.set(key, set);
    }
    set.add(canvas);
  }

  /** Lazily create the scratch GL context; false (sticky) on any failure. */
  private ensureGL(): boolean {
    if (this.gl) return true;
    if (this.glFailed) return false;
    if (typeof document === "undefined") {
      this.glFailed = true;
      return false;
    }
    try {
      const canvas = document.createElement("canvas");
      canvas.width = MEDALLION_PX;
      canvas.height = MEDALLION_PX;
      const renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        preserveDrawingBuffer: false,
      });
      renderer.setSize(MEDALLION_PX, MEDALLION_PX, false);
      renderer.setClearColor(0x000000, 0);
      const scene = new THREE.Scene();
      // Portrait key/rim/fill, roughly matching the day-lit battlefield.
      const key = new THREE.DirectionalLight(0xfff2dd, 1.7);
      key.position.set(2, 3, 2.5);
      const rim = new THREE.DirectionalLight(0x88aaff, 0.6);
      rim.position.set(-2.5, 1.2, -2);
      scene.add(key, rim, new THREE.AmbientLight(0xffffff, 0.55));
      // Head-and-shoulders framing; the figure faces +z.
      const camera = new THREE.PerspectiveCamera(30, 1, 0.05, 20);
      camera.position.set(0.3, 1.55, 2.6);
      camera.lookAt(0, 1.25, 0);
      const group = new THREE.Group();
      scene.add(group);
      this.gl = {
        renderer,
        scene,
        camera,
        group,
        subjects: new Map(),
        attached: new Map(),
      };
      // Warm up the shader so the first visible frame is complete.
      const warmSpec: MedallionSpec = {
        artKey: "placeholder:warmup",
        name: "?",
        tint: 0xb98b3a,
      };
      this.renderFrame(0.45, -0.12, 0xb98b3a, warmSpec);
      const warmKey = medallionCacheKey(warmSpec);
      const warm = this.gl.subjects.get(warmKey);
      if (warm) this.gl.group.remove(warm.holder);
      this.gl.subjects.delete(warmKey);
      return true;
    } catch {
      this.glFailed = true;
      this.gl = null;
      return false;
    }
  }

  /**
   * The portrait subject for a spec: the unit's own 3D model. Without a
   * vendored GLB this is the procedural skinned humanoid (faction-tinted,
   * bone-posible for the intro sweep). With one, the loaded GLB scene
   * upgrades the subject asynchronously — the procedural portrait shows
   * until the load completes, then the cached frame is re-rendered and
   * every attached canvas is repainted.
   */
  private ensureSubject(spec: MedallionSpec): PortraitSubject {
    const g = this.gl!;
    const key = medallionCacheKey(spec);
    let subject = g.subjects.get(key);
    if (subject) return subject;
    const holder = new THREE.Group();
    const geo = buildHumanoidGeometry();
    const attrs = attachAnimAttributes(geo, 1);
    const mat = makeSkinnedMaterial(this.anim.texture, BONE_COUNT, FRAMES_PER_CLIP);
    const mesh = new THREE.InstancedMesh(geo, mat, 1);
    mesh.frustumCulled = false;
    mesh.setMatrixAt(0, new THREE.Matrix4());
    mesh.instanceMatrix.needsUpdate = true;
    holder.add(mesh);
    holder.visible = false;
    g.group.add(holder);
    subject = { holder, procedural: true, attrs, mat, mixer: null };
    g.subjects.set(key, subject);
    // MODEL-SWAP HOOK (implemented): vendored GLB replaces the procedural
    // subject. Clone rule: the store's cached scene is shared — clone per
    // subject, never mutate it.
    const model = spec.model;
    if (model?.available && this.store) {
      void this.store.load(model).then((scene) => {
        const current = g.subjects.get(key);
        if (!scene || !current || !current.procedural) return;
        try {
          const fitted = fitPortraitModel(scene);
          // Swap the subject contents; keep the holder (rotation target).
          current.holder.clear();
          current.holder.add(fitted);
          current.procedural = false;
          current.attrs = null;
          current.mat = null;
          // REAL-CLIP INTRO: if the export vendors skeletal clips, drive
          // them natively on the clone during the intro (poseFrame advances
          // the mixer). First clip wins — the kits vendor a single Attack
          // each; it loops for the 1.6 s intro, then freezes.
          void this.store!.loadAnimationClips(model).then((clips) => {
            const live = g.subjects.get(key);
            if (!live || live.procedural || clips.length === 0) return;
            try {
              const mixer = new THREE.AnimationMixer(fitted);
              const action = mixer.clipAction(clips[0]);
              action.setLoop(THREE.LoopRepeat, Infinity);
              action.play();
              live.mixer = mixer;
            } catch {
              // Keep the static GLB portrait.
            }
          });
          this.refreshCachedFrame(key, spec);
        } catch (e) {
          console.warn(`[medallions] failed to fit ${model.webPath}: ${String(e)}`);
        }
      });
    }
    return subject;
  }

  /** Re-render a cached frame (after a GLB upgrade) and repaint canvases. */
  private refreshCachedFrame(key: string, spec: MedallionSpec): void {
    const g = this.gl;
    if (!g) return;
    try {
      const frame = this.renderFrame(0.45, -0.12, spec.tint, spec);
      this.cache.set(key, frame);
      for (const canvas of g.attached.get(key) ?? []) {
        if (!canvas.isConnected) continue;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
      }
    } catch {
      // Keep the older cached frame; nothing visible breaks.
    }
  }

  /** Show only this subject; hide the rest. Returns null (never throws) when
   * the subject was never ensured — callers treat a missing subject as a
   * skipped frame rather than an uncaught exception in the rAF loop. */
  private showSubject(g: PortraitGL, key: string): PortraitSubject | null {
    const subject = g.subjects.get(key) ?? null;
    for (const [k, s] of g.subjects) s.holder.visible = k === key;
    return subject;
  }

  /** Write one animated pose into the subject (procedural subjects only). */
  private poseFrame(
    g: PortraitGL,
    spec: MedallionSpec,
    slotA: number,
    timeA: number,
    slotB: number,
    timeB: number,
    blend: number,
    rotY: number,
    dt: number,
  ): void {
    const key = medallionCacheKey(spec);
    const subject = this.showSubject(g, key);
    if (!subject) return; // subject never ensured: skip the frame, don't throw
    if (subject.procedural && subject.attrs) {
      const durA = Math.max(1e-6, this.anim.durationOf(slotA));
      const durB = Math.max(1e-6, this.anim.durationOf(slotB));
      const frameA = ((timeA % durA) / durA) * (FRAMES_PER_CLIP - 1);
      const frameB = ((timeB % durB) / durB) * (FRAMES_PER_CLIP - 1);
      writeAnimPose(subject.attrs, 0, slotA, frameA, slotB, frameB, blend);
      flagAnimAttributes(subject.attrs);
    } else if (subject.mixer) {
      // Real-clip intro: advance the GLB's own skeletal clip natively.
      subject.mixer.update(dt);
    }
    g.group.rotation.y = rotY;
  }

  /** Render one idle-pose frame to a fresh 2D canvas (for the cache). */
  private renderFrame(
    idleTime: number,
    rotY: number,
    tint: number,
    spec: MedallionSpec,
  ): HTMLCanvasElement {
    const g = this.gl;
    if (!g) throw new Error("medallion GL not initialized");
    const key = medallionCacheKey(spec);
    this.ensureSubject(spec);
    const subject = this.showSubject(g, key);
    if (!subject) throw new Error("medallion subject missing right after ensure");
    if (subject.procedural && subject.attrs && subject.mat) {
      subject.mat.color.set(tint);
      const slot = this.anim.clipIndexFor("idle" as AnimActivity, false);
      const dur = Math.max(1e-6, this.anim.durationOf(slot));
      const frame = ((idleTime % dur) / dur) * (FRAMES_PER_CLIP - 1);
      writeAnimPose(subject.attrs, 0, slot, frame, slot, frame, 0);
      flagAnimAttributes(subject.attrs);
    }
    g.group.rotation.y = rotY;
    g.renderer.render(g.scene, g.camera);
    const out = document.createElement("canvas");
    out.width = MEDALLION_PX;
    out.height = MEDALLION_PX;
    const ctx = out.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    ctx.drawImage(g.renderer.domElement, 0, 0);
    return out;
  }
}

/**
 * Fit a unit GLB scene into the portrait framing: ~1.7 m tall, grounded at
 * y=0, centered — the same head-and-shoulders framing the procedural
 * humanoid uses. Clone rule: the input scene is shared — clone it.
 */
export function fitPortraitModel(scene: THREE.Group): THREE.Group {
  const holder = new THREE.Group();
  const inner = scene.clone(true);
  holder.add(inner);
  inner.updateMatrixWorld(true);
  const bbox = new THREE.Box3().setFromObject(inner);
  const size = new THREE.Vector3();
  bbox.getSize(size);
  const center = new THREE.Vector3();
  bbox.getCenter(center);
  const s = size.y > 1e-6 ? 1.7 / size.y : 1;
  inner.scale.setScalar(s);
  inner.updateMatrixWorld(true);
  const fitted = new THREE.Box3().setFromObject(inner);
  const c2 = new THREE.Vector3();
  fitted.getCenter(c2);
  inner.position.x -= c2.x;
  inner.position.z -= c2.z;
  inner.position.y -= fitted.min.y;
  return holder;
}
